import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentPortfolio.css";

const EMPTY_FORM = {
  project_id: "",
  title: "",
  slug: "",
  client_display_name: "",
  project_type: "website",
  short_description: "",
  full_description: "",
  technologies: "",
  features: "",
  image_url: "",
  thumbnail_url: "",
  project_url: "",
  repository_url: "",
  completed_date: "",
  featured: false,
  active: true,
  display_order: "0",
};

const PROJECT_TYPES = [
  "website",
  "website_redesign",
  "web_application",
  "ecommerce",
  "automation",
  "maintenance",
  "consulting",
  "other",
];

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function optionalText(value) {
  const cleaned =
    cleanText(value);

  return cleaned || null;
}

function normalizeDate(value) {
  return value
    ? safeString(value).slice(
        0,
        10
      )
    : "";
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(
    `${normalizeDate(
      value
    )}T00:00:00`
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function createSlug(value) {
  return safeString(value)
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(
      /[^a-z0-9]+/g,
      "-"
    )
    .replace(
      /^-+|-+$/g,
      ""
    );
}

function getClientName(
  project
) {
  if (!project) {
    return "";
  }

  const personalName = [
    project.first_name,
    project.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    project.business_name ||
    personalName ||
    ""
  );
}

function normalizePortfolioProject(
  portfolioProject = {}
) {
  return {
    project_id:
      portfolioProject.project_id ==
      null
        ? ""
        : String(
            portfolioProject.project_id
          ),

    title:
      safeString(
        portfolioProject.title
      ),

    slug:
      safeString(
        portfolioProject.slug
      ),

    client_display_name:
      safeString(
        portfolioProject.client_display_name
      ),

    project_type:
      safeString(
        portfolioProject.project_type
      ) || "website",

    short_description:
      safeString(
        portfolioProject.short_description
      ),

    full_description:
      safeString(
        portfolioProject.full_description
      ),

    technologies:
      safeString(
        portfolioProject.technologies
      ),

    features:
      safeString(
        portfolioProject.features
      ),

    image_url:
      safeString(
        portfolioProject.image_url
      ),

    thumbnail_url:
      safeString(
        portfolioProject.thumbnail_url
      ),

    project_url:
      safeString(
        portfolioProject.project_url
      ),

    repository_url:
      safeString(
        portfolioProject.repository_url
      ),

    completed_date:
      normalizeDate(
        portfolioProject.completed_date
      ),

    featured:
      Number(
        portfolioProject.featured
      ) === 1,

    active:
      Number(
        portfolioProject.active
      ) === 1,

    display_order:
      portfolioProject.display_order ==
      null
        ? "0"
        : String(
            portfolioProject.display_order
          ),
  };
}

function DevelopmentPortfolio() {
  const [
    portfolioProjects,
    setPortfolioProjects,
  ] = useState([]);

  const [projects, setProjects] =
    useState([]);

  const [
    selectedPortfolioProject,
    setSelectedPortfolioProject,
  ] = useState(null);

  const [formData, setFormData] =
    useState({
      ...EMPTY_FORM,
    });

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("active");

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          portfolioResponse,
          projectResponse,
        ] = await Promise.all([
          Api.getDevelopmentPortfolioProjects(),
          Api.getDevelopmentProjects(),
        ]);

        setPortfolioProjects(
          portfolioResponse
            ?.portfolio_projects ||
            portfolioResponse
              ?.portfolioProjects ||
            portfolioResponse
              ?.projects ||
            []
        );

        setProjects(
          projectResponse?.projects ||
            []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Portfolio projects could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map(
          (project) => [
            Number(project.id),
            project,
          ]
        )
      ),
    [projects]
  );

  const filteredProjects =
    useMemo(() => {
      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      return portfolioProjects.filter(
        (portfolioProject) => {
          const isActive =
            Number(
              portfolioProject.active
            ) === 1;

          const isFeatured =
            Number(
              portfolioProject.featured
            ) === 1;

          if (
            statusFilter ===
              "active" &&
            !isActive
          ) {
            return false;
          }

          if (
            statusFilter ===
              "inactive" &&
            isActive
          ) {
            return false;
          }

          if (
            statusFilter ===
              "featured" &&
            !isFeatured
          ) {
            return false;
          }

          if (
            typeFilter &&
            String(
              portfolioProject.project_type
            ) !== typeFilter
          ) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          const linkedProject =
            projectMap.get(
              Number(
                portfolioProject.project_id
              )
            );

          const searchableText = [
            portfolioProject.title,
            portfolioProject.slug,
            portfolioProject.client_display_name,
            portfolioProject.project_type,
            portfolioProject.short_description,
            portfolioProject.full_description,
            portfolioProject.technologies,
            portfolioProject.features,
            linkedProject?.title,
            getClientName(
              linkedProject
            ),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(
            normalizedSearch
          );
        }
      );
    }, [
      portfolioProjects,
      search,
      statusFilter,
      typeFilter,
      projectMap,
    ]);

  const orderedProjects =
    useMemo(
      () =>
        [...portfolioProjects].sort(
          (a, b) =>
            Number(
              a.display_order || 0
            ) -
              Number(
                b.display_order || 0
              ) ||
            Number(a.id) -
              Number(b.id)
        ),
      [portfolioProjects]
    );

  const summary = useMemo(
    () => ({
      total:
        portfolioProjects.length,

      active:
        portfolioProjects.filter(
          (item) =>
            Number(item.active) ===
            1
        ).length,

      inactive:
        portfolioProjects.filter(
          (item) =>
            Number(item.active) !==
            1
        ).length,

      featured:
        portfolioProjects.filter(
          (item) =>
            Number(
              item.featured
            ) === 1
        ).length,
    }),
    [portfolioProjects]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewForm = () => {
    clearMessages();

    setSelectedPortfolioProject(
      null
    );

    setFormData({
      ...EMPTY_FORM,
    });

    setShowForm(true);
  };

  const openEditForm = (
    portfolioProject
  ) => {
    clearMessages();

    setSelectedPortfolioProject(
      portfolioProject
    );

    setFormData(
      normalizePortfolioProject(
        portfolioProject
      )
    );

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedPortfolioProject(
      null
    );

    setFormData({
      ...EMPTY_FORM,
    });

    setShowForm(false);
  };

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    setFormData((current) => {
      const next = {
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (
        name === "title" &&
        !selectedPortfolioProject
      ) {
        next.slug =
          createSlug(value);
      }

      if (
        name === "active" &&
        !checked
      ) {
        next.featured = false;
      }

      if (
        name === "featured" &&
        checked
      ) {
        next.active = true;
      }

      if (
        name === "project_id" &&
        value
      ) {
        const project =
          projectMap.get(
            Number(value)
          );

        if (project) {
          const clientName =
            getClientName(
              project
            );

          next.title =
            cleanText(
              current.title
            ) ||
            safeString(
              project.title
            );

          next.slug =
            cleanText(
              current.slug
            ) ||
            createSlug(
              project.title
            );

          next.project_type =
            safeString(
              project.project_type
            ) ||
            current.project_type ||
            "website";

          next.client_display_name =
            cleanText(
              current.client_display_name
            ) ||
            clientName;

          next.short_description =
            cleanText(
              current.short_description
            ) ||
            safeString(
              project.description
            );

          next.full_description =
            cleanText(
              current.full_description
            ) ||
            safeString(
              project.description
            );

          next.completed_date =
            current.completed_date ||
            normalizeDate(
              project.actual_completion_date
            );
        }
      }

      return next;
    });
  };

  const validateForm = () => {
    if (
      !cleanText(
        formData.title
      )
    ) {
      return "Portfolio title is required.";
    }

    if (
      !createSlug(
        formData.slug
      )
    ) {
      return "Portfolio slug is required.";
    }

    const displayOrder =
      Number(
        formData.display_order
      );

    if (
      !Number.isInteger(
        displayOrder
      ) ||
      displayOrder < 0
    ) {
      return "Display order must be a whole number that is 0 or greater.";
    }

    return "";
  };

  const buildPayload = () => ({
    project_id:
      formData.project_id
        ? Number(
            formData.project_id
          )
        : null,

    title:
      cleanText(
        formData.title
      ),

    slug:
      createSlug(
        formData.slug
      ),

    client_display_name:
      optionalText(
        formData.client_display_name
      ),

    project_type:
      cleanText(
        formData.project_type
      ) || "website",

    short_description:
      optionalText(
        formData.short_description
      ),

    full_description:
      optionalText(
        formData.full_description
      ),

    technologies:
      optionalText(
        formData.technologies
      ),

    features:
      optionalText(
        formData.features
      ),

    image_url:
      optionalText(
        formData.image_url
      ),

    thumbnail_url:
      optionalText(
        formData.thumbnail_url
      ),

    project_url:
      optionalText(
        formData.project_url
      ),

    repository_url:
      optionalText(
        formData.repository_url
      ),

    completed_date:
      normalizeDate(
        formData.completed_date
      ) || null,

    featured:
      Boolean(
        formData.featured
      ),

    active:
      Boolean(
        formData.active
      ),

    display_order:
      Number(
        formData.display_order ||
          0
      ),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (
        selectedPortfolioProject
      ) {
        await Api.updateDevelopmentPortfolioProject(
          selectedPortfolioProject.id,
          payload
        );

        setSuccess(
          "Portfolio project updated successfully."
        );
      } else {
        await Api.createDevelopmentPortfolioProject(
          payload
        );

        setSuccess(
          "Portfolio project created successfully."
        );
      }

      await loadData();
      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The portfolio project could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updatePortfolioProject =
    async (
      portfolioProject,
      changes,
      message
    ) => {
      clearMessages();

      try {
        await Api.updateDevelopmentPortfolioProject(
          portfolioProject.id,
          changes
        );

        setSuccess(message);

        await loadData();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The portfolio project could not be updated."
        );
      }
    };

  const toggleActive = (
    portfolioProject
  ) => {
    const nextActive =
      Number(
        portfolioProject.active
      ) !== 1;

    updatePortfolioProject(
      portfolioProject,
      {
        active: nextActive,

        featured:
          nextActive
            ? Number(
                portfolioProject.featured
              ) === 1
            : false,
      },
      nextActive
        ? "Portfolio project activated."
        : "Portfolio project deactivated."
    );
  };

  const toggleFeatured = (
    portfolioProject
  ) => {
    const nextFeatured =
      Number(
        portfolioProject.featured
      ) !== 1;

    updatePortfolioProject(
      portfolioProject,
      {
        featured:
          nextFeatured,

        active:
          nextFeatured
            ? true
            : Number(
                portfolioProject.active
              ) === 1,
      },
      nextFeatured
        ? "Portfolio project featured."
        : "Portfolio project removed from featured projects."
    );
  };

  const moveProject = async (
    portfolioProject,
    direction
  ) => {
    const currentIndex =
      orderedProjects.findIndex(
        (item) =>
          Number(item.id) ===
          Number(
            portfolioProject.id
          )
      );

    const targetIndex =
      currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >=
        orderedProjects.length
    ) {
      return;
    }

    const target =
      orderedProjects[
        targetIndex
      ];

    let currentOrder =
      Number(
        portfolioProject.display_order ||
          0
      );

    let targetOrder =
      Number(
        target.display_order || 0
      );

    /*
     * When both records have the same order,
     * assign a distinct value so the movement
     * remains visible after reloading.
     */
    if (
      currentOrder === targetOrder
    ) {
      currentOrder =
        currentIndex;

      targetOrder =
        targetIndex;
    }

    clearMessages();

    try {
      await Promise.all([
        Api.updateDevelopmentPortfolioProject(
          portfolioProject.id,
          {
            display_order:
              targetOrder,
          }
        ),

        Api.updateDevelopmentPortfolioProject(
          target.id,
          {
            display_order:
              currentOrder,
          }
        ),
      ]);

      setSuccess(
        "Portfolio display order updated."
      );

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The display order could not be updated."
      );
    }
  };

  const deletePortfolioProject =
    async (
      portfolioProject
    ) => {
      const confirmed =
        window.confirm(
          `Permanently delete portfolio project "${portfolioProject.title}"? This will not delete the linked client project.`
        );

      if (!confirmed) {
        return;
      }

      clearMessages();

      try {
        const response =
          await Api.deleteDevelopmentPortfolioProject(
            portfolioProject.id
          );

        setSuccess(
          response?.message ||
            "Portfolio project deleted successfully."
        );

        if (
          Number(
            selectedPortfolioProject?.id
          ) ===
          Number(
            portfolioProject.id
          )
        ) {
          closeForm();
        }

        await loadData();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The portfolio project could not be deleted."
        );
      }
    };

  return (
    <div className="development-portfolio-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Portfolio management
          </p>

          <h2>Portfolio</h2>

          <p>
            Manage completed projects shown
            publicly on the Avwakin Development
            website.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={openNewForm}
          >
            Add portfolio project
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <PortfolioForm
          formData={formData}
          projects={projects}
          selectedPortfolioProject={
            selectedPortfolioProject
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-portfolio-summary-grid">
        <SummaryCard
          label="All projects"
          value={summary.total}
          active={
            statusFilter === "all"
          }
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Active"
          value={summary.active}
          active={
            statusFilter ===
            "active"
          }
          onClick={() =>
            setStatusFilter(
              "active"
            )
          }
        />

        <SummaryCard
          label="Inactive"
          value={summary.inactive}
          active={
            statusFilter ===
            "inactive"
          }
          onClick={() =>
            setStatusFilter(
              "inactive"
            )
          }
        />

        <SummaryCard
          label="Featured"
          value={summary.featured}
          active={
            statusFilter ===
            "featured"
          }
          onClick={() =>
            setStatusFilter(
              "featured"
            )
          }
        />
      </section>

      <section className="development-portfolio-panel">
        <div className="development-portfolio-toolbar">
          <div>
            <h3>
              Portfolio projects
            </h3>

            <p>
              {filteredProjects.length}{" "}
              project
              {filteredProjects.length ===
              1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-portfolio-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search portfolio..."
              aria-label="Search portfolio"
            />

            <select
              value={typeFilter}
              onChange={(event) =>
                setTypeFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All project types
              </option>

              {PROJECT_TYPES.map(
                (projectType) => (
                  <option
                    key={projectType}
                    value={projectType}
                  >
                    {formatLabel(
                      projectType
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All projects
              </option>

              <option value="active">
                Active
              </option>

              <option value="inactive">
                Inactive
              </option>

              <option value="featured">
                Featured
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading portfolio projects...
          </div>
        ) : filteredProjects.length ===
          0 ? (
          <div className="development-admin-empty">
            No portfolio projects match the
            current filters.
          </div>
        ) : (
          <div className="development-portfolio-card-grid">
            {filteredProjects.map(
              (
                portfolioProject
              ) => (
                <PortfolioCard
                  key={
                    portfolioProject.id
                  }
                  portfolioProject={
                    portfolioProject
                  }
                  linkedProject={
                    projectMap.get(
                      Number(
                        portfolioProject.project_id
                      )
                    )
                  }
                  onEdit={() =>
                    openEditForm(
                      portfolioProject
                    )
                  }
                  onToggleActive={() =>
                    toggleActive(
                      portfolioProject
                    )
                  }
                  onToggleFeatured={() =>
                    toggleFeatured(
                      portfolioProject
                    )
                  }
                  onMoveUp={() =>
                    moveProject(
                      portfolioProject,
                      -1
                    )
                  }
                  onMoveDown={() =>
                    moveProject(
                      portfolioProject,
                      1
                    )
                  }
                  onDelete={() =>
                    deletePortfolioProject(
                      portfolioProject
                    )
                  }
                />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-portfolio-summary-card${
        active
          ? " active"
          : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>

      <strong>{value}</strong>
    </button>
  );
}

function PortfolioCard({
  portfolioProject,
  linkedProject,
  onEdit,
  onToggleActive,
  onToggleFeatured,
  onMoveUp,
  onMoveDown,
  onDelete,
}) {
  const active =
    Number(
      portfolioProject.active
    ) === 1;

  const featured =
    Number(
      portfolioProject.featured
    ) === 1;

  const previewImage =
    portfolioProject.thumbnail_url ||
    portfolioProject.image_url;

  return (
    <article
      className={`development-portfolio-card${
        !active
          ? " inactive"
          : ""
      }`}
    >
      <div className="development-portfolio-image-wrapper">
        {previewImage ? (
          <img
            src={previewImage}
            alt={
              portfolioProject.title ||
              "Portfolio project"
            }
          />
        ) : (
          <div className="development-portfolio-image-placeholder">
            No image
          </div>
        )}

        <div className="development-portfolio-card-badges">
          {featured && (
            <span className="featured">
              Featured
            </span>
          )}

          <span
            className={
              active
                ? "active"
                : "inactive"
            }
          >
            {active
              ? "Active"
              : "Inactive"}
          </span>
        </div>
      </div>

      <div className="development-portfolio-card-content">
        <p className="development-portfolio-order">
          Display order:{" "}
          {portfolioProject.display_order ||
            0}
        </p>

        <h4>
          {portfolioProject.title}
        </h4>

        <p className="development-portfolio-client">
          {portfolioProject.client_display_name ||
            "Client not displayed"}
        </p>

        <div className="development-portfolio-meta-grid">
          <div>
            <span>Type</span>

            <strong>
              {formatLabel(
                portfolioProject.project_type ||
                  "other"
              )}
            </strong>
          </div>

          <div>
            <span>Completed</span>

            <strong>
              {formatDate(
                portfolioProject.completed_date
              )}
            </strong>
          </div>

          <div>
            <span>
              Linked project
            </span>

            <strong>
              {linkedProject?.title ||
                portfolioProject.linked_project_title ||
                "—"}
            </strong>
          </div>

          <div>
            <span>Slug</span>

            <strong>
              {portfolioProject.slug}
            </strong>
          </div>
        </div>

        <p className="development-portfolio-description">
          {portfolioProject.short_description ||
            "No short description has been added."}
        </p>

        <div className="development-portfolio-links">
          {portfolioProject.project_url && (
            <a
              href={
                portfolioProject.project_url
              }
              target="_blank"
              rel="noreferrer"
            >
              View project
            </a>
          )}

          {portfolioProject.repository_url && (
            <a
              href={
                portfolioProject.repository_url
              }
              target="_blank"
              rel="noreferrer"
            >
              Repository
            </a>
          )}
        </div>

        <div className="development-portfolio-card-footer">
          <div className="development-portfolio-order-actions">
            <button
              type="button"
              onClick={onMoveUp}
              aria-label={`Move ${portfolioProject.title} up`}
            >
              ↑
            </button>

            <button
              type="button"
              onClick={onMoveDown}
              aria-label={`Move ${portfolioProject.title} down`}
            >
              ↓
            </button>
          </div>

          <div className="development-portfolio-card-actions">
            <button
              type="button"
              onClick={onEdit}
            >
              Edit
            </button>

            <button
              type="button"
              className={
                featured
                  ? "unfeature"
                  : "feature"
              }
              onClick={
                onToggleFeatured
              }
            >
              {featured
                ? "Unfeature"
                : "Feature"}
            </button>

            <button
              type="button"
              className={
                active
                  ? "deactivate"
                  : "activate"
              }
              onClick={
                onToggleActive
              }
            >
              {active
                ? "Deactivate"
                : "Activate"}
            </button>

            <button
              type="button"
              className="delete"
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function PortfolioForm({
  formData,
  projects,
  selectedPortfolioProject,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-portfolio-form-panel">
      <div className="development-portfolio-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedPortfolioProject
              ? "Edit portfolio project"
              : "New portfolio project"}
          </p>

          <h3>
            {selectedPortfolioProject
              ? selectedPortfolioProject.title
              : "Create portfolio project"}
          </h3>
        </div>

        <button
          type="button"
          className="development-portfolio-close-button"
          onClick={onCancel}
          aria-label="Close portfolio form"
        >
          ×
        </button>
      </div>

      <form
        className="development-portfolio-form"
        onSubmit={onSubmit}
      >
        <div className="development-portfolio-form-grid">
          <SelectField
            label="Linked project"
            name="project_id"
            value={
              formData.project_id
            }
            onChange={onChange}
          >
            <option value="">
              No linked project
            </option>

            {projects.map(
              (project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.title}
                </option>
              )
            )}
          </SelectField>

          <SelectField
            label="Project type"
            name="project_type"
            value={
              formData.project_type
            }
            onChange={onChange}
          >
            {PROJECT_TYPES.map(
              (projectType) => (
                <option
                  key={projectType}
                  value={projectType}
                >
                  {formatLabel(
                    projectType
                  )}
                </option>
              )
            )}
          </SelectField>

          <FormField
            label="Title"
            name="title"
            value={formData.title}
            onChange={onChange}
            required
            fullWidth
          />

          <FormField
            label="Slug"
            name="slug"
            value={formData.slug}
            onChange={onChange}
            required
          />

          <FormField
            label="Client display name"
            name="client_display_name"
            value={
              formData.client_display_name
            }
            onChange={onChange}
          />

          <FormField
            label="Completed date"
            name="completed_date"
            type="date"
            value={
              formData.completed_date
            }
            onChange={onChange}
          />

          <FormField
            label="Display order"
            name="display_order"
            type="number"
            value={
              formData.display_order
            }
            onChange={onChange}
            min="0"
            step="1"
          />

          <TextAreaField
            label="Short description"
            name="short_description"
            value={
              formData.short_description
            }
            onChange={onChange}
            rows="4"
          />

          <TextAreaField
            label="Full description"
            name="full_description"
            value={
              formData.full_description
            }
            onChange={onChange}
            rows="7"
          />

          <TextAreaField
            label="Technologies"
            name="technologies"
            value={
              formData.technologies
            }
            onChange={onChange}
            rows="4"
            placeholder="React, Node.js, Express, MySQL..."
          />

          <TextAreaField
            label="Features"
            name="features"
            value={formData.features}
            onChange={onChange}
            rows="5"
            placeholder="Responsive design, scheduling, admin management..."
          />

          <FormField
            label="Main image URL"
            name="image_url"
            type="url"
            value={
              formData.image_url
            }
            onChange={onChange}
            placeholder="https://"
            fullWidth
          />

          <FormField
            label="Thumbnail URL"
            name="thumbnail_url"
            type="url"
            value={
              formData.thumbnail_url
            }
            onChange={onChange}
            placeholder="https://"
            fullWidth
          />

          <FormField
            label="Project URL"
            name="project_url"
            type="url"
            value={
              formData.project_url
            }
            onChange={onChange}
            placeholder="https://"
            fullWidth
          />

          <FormField
            label="Repository URL"
            name="repository_url"
            type="url"
            value={
              formData.repository_url
            }
            onChange={onChange}
            placeholder="https://"
            fullWidth
          />
        </div>

        <div className="development-portfolio-checkbox-row">
          <CheckboxField
            name="featured"
            label="Featured project"
            checked={
              formData.featured
            }
            onChange={onChange}
          />

          <CheckboxField
            name="active"
            label="Visible on portfolio"
            checked={
              formData.active
            }
            onChange={onChange}
          />
        </div>

        <div className="development-portfolio-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedPortfolioProject
                ? "Save changes"
                : "Create portfolio project"}
          </button>
        </div>
      </form>
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  min,
  max,
  step,
  fullWidth = false,
}) {
  return (
    <label
      className={`development-portfolio-field${
        fullWidth
          ? " full-width"
          : ""
      }`}
    >
      <span>
        {label}

        {required && " *"}
      </span>

      <input
        type={type}
        name={name}
        value={
          safeString(value)
        }
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function SelectField({
  label,
  name,
  value,
  onChange,
  required = false,
  children,
}) {
  return (
    <label className="development-portfolio-field">
      <span>
        {label}

        {required && " *"}
      </span>

      <select
        name={name}
        value={
          safeString(value)
        }
        onChange={onChange}
        required={required}
      >
        {children}
      </select>
    </label>
  );
}

function TextAreaField({
  label,
  name,
  value,
  onChange,
  rows = "4",
  placeholder = "",
}) {
  return (
    <label className="development-portfolio-field full-width">
      <span>{label}</span>

      <textarea
        name={name}
        value={
          safeString(value)
        }
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
      />
    </label>
  );
}

function CheckboxField({
  name,
  label,
  checked,
  onChange,
}) {
  return (
    <label>
      <input
        type="checkbox"
        name={name}
        checked={
          Boolean(checked)
        }
        onChange={onChange}
      />

      <span>{label}</span>
    </label>
  );
}

export default DevelopmentPortfolio;